import React from 'react';
import { useCategoryCard } from '../../hooks/useCategoriesData';

interface ErrorWithMessage {
  message: string;
}

function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return typeof error === 'object' && error !== null && 'message' in error;
}

function getErrorMessage(error: unknown): string {
  if (isErrorWithMessage(error)) {
    return error.message;
  }
  return 'An unknown error occurred';
}

function getCategoryIdFromHash(): string | null {
  const hash = window.location.hash.slice(1);
  const [, queryString] = hash.split('?');
  if (!queryString) return null;
  const params = new URLSearchParams(queryString);
  return params.get('category_id');
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: '1.2rem' }}>
    <h2 style={{ fontSize: '1rem', margin: '0 0 0.35rem', color: '#111827' }}>{title}</h2>
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.85rem' }}>
      {children}
    </div>
  </section>
);

const FieldRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0', borderBottom: '1px dashed #e5e7eb', fontSize: '0.9rem' }}>
    <span style={{ color: '#6b7280', marginRight: '0.75rem', minWidth: '160px' }}>{label}</span>
    <span style={{ flex: 1, textAlign: 'right', color: '#111827' }}>{value || '—'}</span>
  </div>
);

const CategoryCardPrint: React.FC = () => {
  const categoryId = React.useMemo(() => getCategoryIdFromHash(), []);
  const { data, isLoading, isError, error } = useCategoryCard(categoryId);

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#111827', background: '#f9fafb', minHeight: '100vh', padding: '2rem' }}>
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', margin: 0 }}>Category Card</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280' }}>Print-optimized view sourced from Supabase prod schema.</p>
        </div>
        <button
          type="button"
          className="no-print"
          onClick={() => window.print()}
          style={{ padding: '0.6rem 1.2rem', borderRadius: '999px', border: '1px solid #111827', background: '#fde047', color: '#111827', fontWeight: 600, cursor: 'pointer' }}
        >
          Print
        </button>
      </header>

      {isLoading && <p>Loading category…</p>}
      {isError && <p style={{ color: '#b91c1c' }}>{getErrorMessage(error) || 'Failed to load category.'}</p>}

      {data && (
        <div style={{ display: 'grid', gap: '1.1rem', maxWidth: '900px' }}>
          <Section title="Program">
            <FieldRow label="Program name" value={data.program?.name || '—'} />
            <FieldRow label="BACI program code" value={data.program?.baci_program_code || '—'} />
            <FieldRow label="Organization" value={data.program?.awarding_organization || '—'} />
          </Section>

          <Section title="Category">
            <FieldRow label="Name" value={data.category?.name || data.category?.display_name || '—'} />
            <FieldRow label="Description" value={data.category?.description || '—'} />
            <FieldRow label="Display order" value={data.category?.display_order ?? '—'} />
            <FieldRow label="BACI category id" value={data.category?.baci_category_id || '—'} />
          </Section>

          <Section title="Hierarchy">
            <FieldRow label="Tier 1" value={data.hierarchy?.tier_1 || '—'} />
            <FieldRow label="Tier 2" value={data.hierarchy?.tier_2 || '—'} />
            <FieldRow label="Tier 3" value={data.hierarchy?.tier_3 || '—'} />
            <FieldRow label="Path" value={data.hierarchy?.path || '—'} />
          </Section>

          <Section title="Eligibility">
            <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
              {data.eligibility?.eligibility_requirements || '—'}
            </div>
          </Section>

          <Section title="Fees & Prizes">
            <FieldRow label="Entry fee" value={data.fees_prizes?.entry_fee !== undefined ? `$${Number(data.fees_prizes.entry_fee).toFixed(2)}` : '—'} />
            <FieldRow label="Prize details" value={data.fees_prizes?.prize_details || '—'} />
          </Section>

          <Section title="Timeline">
            <FieldRow label="Submission closes" value={data.timeline?.submission_closes ? new Date(data.timeline.submission_closes).toLocaleDateString() : '—'} />
            <FieldRow label="Winners announced" value={data.timeline?.winners_announced ? new Date(data.timeline.winners_announced).toLocaleDateString() : '—'} />
            <FieldRow label="Lifecycle status" value={data.timeline?.lifecycle_status || '—'} />
            <FieldRow label="Recurring annual" value={data.timeline?.recurring_annual === true ? 'Yes' : data.timeline?.recurring_annual === false ? 'No' : '—'} />
          </Section>

          <Section title="Program deadlines">
            <FieldRow label="Deadline structure" value={data.program_deadlines?.deadline_structure || '—'} />
            <FieldRow label="Early bird" value={data.program_deadlines?.early_bird_deadline ? new Date(data.program_deadlines.early_bird_deadline).toLocaleDateString() : '—'} />
            <FieldRow label="Final deadline" value={data.program_deadlines?.final_deadline ? new Date(data.program_deadlines.final_deadline).toLocaleDateString() : '—'} />
            <FieldRow label="Winners announcement" value={data.program_deadlines?.winners_announcement_date ? new Date(data.program_deadlines.winners_announcement_date).toLocaleDateString() : '—'} />
          </Section>

          <Section title="BISAC mappings">
            {Array.isArray(data.bisac_mappings) && data.bisac_mappings.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '0.4rem', border: '1px solid #e5e7eb' }}>Code</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem', border: '1px solid #e5e7eb' }}>Match type</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem', border: '1px solid #e5e7eb' }}>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bisac_mappings.map((mapping, idx) => (
                    <tr key={`${mapping.bisac_code}-${idx}`}>
                      <td style={{ padding: '0.35rem', border: '1px solid #e5e7eb' }}>{mapping.bisac_code}</td>
                      <td style={{ padding: '0.35rem', border: '1px solid #e5e7eb' }}>{mapping.match_type || '—'}</td>
                      <td style={{ padding: '0.35rem', border: '1px solid #e5e7eb' }}>{mapping.confidence !== undefined ? (Number(mapping.confidence) * 100).toFixed(0) + '%' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: '0.9rem' }}>No mappings recorded.</div>
            )}
          </Section>

          <Section title="Ratings & Vetting">
            <FieldRow label="Vetting status" value={data.ratings_vetting?.vetting_status || '—'} />
            <FieldRow label="Priority score" value={data.ratings_vetting?.priority_score ?? '—'} />
            <FieldRow label="Quality score" value={data.ratings_vetting?.data_quality_score ?? '—'} />
            <FieldRow label="Vetting notes" value={Array.isArray(data.ratings_vetting?.vetting_notes) ? `${data.ratings_vetting.vetting_notes.length} notes` : '—'} />
          </Section>
        </div>
      )}
    </div>
  );
};

export default CategoryCardPrint;
